import { useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "../AddressForm";
import { SchedulerWizardFormValues } from "src/interfaces";
import { getFormValues } from "redux-form";
import {
  IconButton,
  IconButtonIcon,
  PickupScheduleSummaryCard,
  PickupScheduleSummaryContent,
  PickupScheduleSummaryLeftContent,
  Separator,
  SeparatorLine,
  TextDisplay,
  Wrapper,
} from "src/styled";
import { translate } from "src/services";
import MapDisplay from "src/components/MapDisplay";

interface AddressSummaryCardProps {
  displayEditButton: boolean;
  onEditClick: () => void;
}

const AddressSummaryCard = ({
  displayEditButton,
  onEditClick,
}: AddressSummaryCardProps) => {
  const { isSaving } = useAppSelector((state) => state.bulkyItemsScheduler);
  const addressValidationError = useAppSelector(
    (state) => state.bulkyItemsScheduler.errors.addressValidation
  );

  const validatedAddress = useAppSelector(
    (state) => state.bulkyItemsScheduler.addressValidation
  );

  const cityCustomErrorValidationMessage =
    useAppSelector(
      (state) =>
        state.citySettings.settings?.vendorSettings.validationErrorMessage
    ) || translate("alertMessages.addressValidationError");

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  if (!thisFormValues?.address) return null;

  return (
    <>
      <Wrapper
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <TextDisplay size="medium" color="grayDarker">
          <b>{translate("wizardPage.pickupLocation")}</b>
        </TextDisplay>
        {displayEditButton && (
          <IconButton
            type="button"
            color="gray"
            onClick={onEditClick}
            padding="xxSmall small"
            disabled={isSaving}
          >
            <IconButtonIcon
              size="medium"
              icon="edit"
              color="black"
              customViewBox="0 0 30 32"
            />

            <TextDisplay size="xSmall" color="black">
              {translate("common.edit")}
            </TextDisplay>
          </IconButton>
        )}
      </Wrapper>
      <Separator height={10} />
      <Wrapper>
        <PickupScheduleSummaryCard hasRightValues={false}>
          <PickupScheduleSummaryLeftContent isBig={true}>
            <MapDisplay
              height={100}
              width={100}
              center={{
                lat: thisFormValues.address.latitude,
                lng: thisFormValues.address.longitude,
              }}
              zoom={15}
              locationPin={{
                lat: thisFormValues.address.latitude,
                lng: thisFormValues.address.longitude,
              }}
            />
          </PickupScheduleSummaryLeftContent>
          <PickupScheduleSummaryContent>
            {thisFormValues.address.formattedAddress && (
              <TextDisplay size="small" color="black">
                <b>{thisFormValues.address.formattedAddress}</b>
              </TextDisplay>
            )}
            <TextDisplay size="xSmall" color="black" margin="xxSmall no no no">
              {`${
                thisFormValues.address.city
                  ? thisFormValues.address.city + ", "
                  : ""
              }${
                thisFormValues.address.state
                  ? thisFormValues.address.state + " "
                  : ""
              }`}
            </TextDisplay>
            {validatedAddress?.customerName && (
              <TextDisplay size="xSmall" margin="xxSmall no no no">
                {`${translate("wizardPage.customerName")}: ${
                  validatedAddress?.customerName
                }`}
              </TextDisplay>
            )}
            {validatedAddress?.customerAccountNumber && (
              <TextDisplay size="xSmall" margin="xxSmall no no no">
                {`${translate("wizardPage.customerAccountNumber")}: ${
                  validatedAddress?.customerAccountNumber
                }`}
              </TextDisplay>
            )}
            {validatedAddress?.locationName && (
              <TextDisplay size="xSmall" margin="xxSmall no no no">
                {`${translate("wizardPage.locationName")}: ${
                  validatedAddress?.locationName
                }`}
              </TextDisplay>
            )}
            {validatedAddress?.locationAccountNumber && (
              <TextDisplay size="xSmall" margin="xxSmall no no no">
                {`${translate("wizardPage.locationAccountNumber")}: ${
                  validatedAddress?.locationAccountNumber
                }`}
              </TextDisplay>
            )}
          </PickupScheduleSummaryContent>
        </PickupScheduleSummaryCard>
        {addressValidationError && (
          <TextDisplay
            margin="small no medium"
            block
            color="alert"
            size="xSmall"
          >
            <em>{cityCustomErrorValidationMessage}</em>
          </TextDisplay>
        )}
      </Wrapper>
      <Separator height={30} />
      <SeparatorLine />
      <Separator height={30} />
    </>
  );
};

export default AddressSummaryCard;
